/**
 * BLU-880 — bridge-fetch lifecycle vocabulary for the Print File Issues UI.
 *
 * Mirrors the backend contract (`lib/bridge-fetch-progress.ts`): the issue
 * row carries `bridge_fetch_state` / `bridge_fetch_state_at` /
 * `bridge_fetch_fail_reason`, and the `/events/print-files` feed mirrors every
 * stamp as `print_file_bridge_progress`. The bridge-progress stepper reads
 * BOTH (SSE first, `BRIDGE_FETCH_POLL_MS` on the single-row read as the
 * fallback) and merges them with `pickLaterSnapshot`, so whichever source is
 * ahead wins and a stale poll can never drag a live state backwards. The
 * per-side target matrix is NOT polled — see `PrintFileSelfHealForm`.
 *
 * Pure — no React, no I/O — so it is unit-testable in isolation.
 */

/** The server-stamped states, in pipeline order. */
export type BridgeFetchState = 'queued' | 'served' | 'downloaded' | 'ripped' | 'uploaded' | 'failed';
/** Plus the terminal success the resolved event provides. */
export type BridgeFetchUiState = BridgeFetchState | 'resolved';

/** The stepper rows, in display order. `failed` is not a row — it paints the
 *  row it happened on red (see `inferFailedStep`). */
export const BRIDGE_FETCH_STEPS = [
  'queued',
  'served',
  'downloaded',
  'ripped',
  'uploaded',
  'resolved',
] as const;
export type BridgeFetchStep = (typeof BRIDGE_FETCH_STEPS)[number];

export interface BridgeFetchSnapshot {
  state: BridgeFetchUiState;
  /** Server stamp (ISO). Null only for a client-side optimistic reset. */
  at: string | null;
  /** Set with `failed` only. */
  reason: string | null;
}

/** Bridge poll interval is 60s; 2.5× it before we say the bridge has not
 *  picked a `queued` job up. */
export const BRIDGE_FETCH_STALE_MS = 150_000;
/** BLU-1309 (F1b): once the bridge has picked a job up, a large file can take
 *  minutes between steps. Never call it quiet before the backend's 5 minute
 *  hand-out lease (`BRIDGE_HANDOUT_LEASE_MS`) runs out. */
export const BRIDGE_FETCH_WORKING_STALE_MS = 5 * 60_000;

/** How long a state may sit unchanged before the dialog calls it stale. */
export function bridgeFetchStaleMs(state: BridgeFetchUiState | null | undefined): number {
  return state === 'queued' || state == null ? BRIDGE_FETCH_STALE_MS : BRIDGE_FETCH_WORKING_STALE_MS;
}
/** Polling fallback cadence for the issue read model while a fetch is in flight. */
export const BRIDGE_FETCH_POLL_MS = 15_000;

const UI_STATES: readonly string[] = [...BRIDGE_FETCH_STEPS, 'failed'];

export function isBridgeFetchUiState(value: unknown): value is BridgeFetchUiState {
  return typeof value === 'string' && UI_STATES.includes(value);
}

export function isTerminalBridgeFetchState(state: BridgeFetchUiState): boolean {
  return state === 'resolved' || state === 'failed';
}

/** Ordinal rank — used only when a server timestamp is missing on one side. */
function rank(state: BridgeFetchUiState): number {
  if (state === 'failed') return 5;
  if (state === 'resolved') return 6;
  return BRIDGE_FETCH_STEPS.indexOf(state);
}

/** The states that may legitimately RESET a run: a fresh requeue, or a give-up. */
function isRunReset(state: BridgeFetchUiState): boolean {
  return state === 'queued' || state === 'failed';
}

/**
 * Merge an incoming snapshot (SSE or poll) into the current one.
 *
 * `resolved` always wins — the issue is closed, nothing later can matter.
 * An OLDER server stamp never wins (a stale poll can't drag SSE backwards).
 * Among later stamps the merge is RANK-AWARE within a run: the queue
 * re-serves an open issue on every poll, so a later `served` must not beat
 * an earlier `downloaded`/`ripped`/`uploaded`; only `queued` (a fresh
 * requeue) or `failed` may reset a run, and anything later replaces a
 * `failed`. A same-rank re-stamp refreshes to the incoming timestamp. When
 * either side has no stamp (the optimistic client reset), fall back to rank.
 */
export function pickLaterSnapshot(
  current: BridgeFetchSnapshot | null,
  incoming: BridgeFetchSnapshot,
): BridgeFetchSnapshot {
  if (!current) return incoming;
  if (current.state === 'resolved') return current;
  if (incoming.state === 'resolved') return incoming;
  if (current.at && incoming.at) {
    if (incoming.at < current.at) return current;
    if (current.state === 'failed' || isRunReset(incoming.state)) return incoming;
    return rank(incoming.state) >= rank(current.state) ? incoming : current;
  }
  return rank(incoming.state) >= rank(current.state) ? incoming : current;
}

/**
 * Reason codes a `failed` stamp can carry that have a friendly translation
 * (`issues.printFiles.bridgeFetch.reason.<code>`). Unknown codes render the
 * generic failure line plus the raw code.
 *
 * TWO writers. The DAEMON reports only DETERMINISTIC failures (`routing_*`,
 * `no_filename`, `no_source_url`) — transient ones (download / resize / drop /
 * upload / map collision) auto-retry and are never posted; they stay mapped
 * for older daemons. The BACKEND stamps the give-ups: the queue's own poison
 * reasons (`dual_unsupported`, `back_only`, `side_mismatch`,
 * `back_stem_unsupported`, the terminal lookup statuses) fail before the
 * bridge ever downloads, so they paint "Picked up" (as does
 * `png_only_no_server_heal`, the queue refusing a file only the server heal
 * could have made at a floor where that heal is off); `bridge_gave_up` is the
 * attempt cap running out on TRANSIENT errors and can land after ANY
 * progress the bridge already reported, so it has no fixed row (see
 * `inferFailedStep`).
 *
 * BLU-663 adds a THIRD group: the standard-output contract HOLDS. Those codes
 * ride an `awaiting_bridge` stamp, not a `failed` one, so they never reach the
 * stepper's failure line — they surface as the list row's awaiting-bridge hint
 * (`awaitingBridgeHoldReason` below, which resolves through the same parser, so
 * the codes must be registered here too). `invalid_standard_targets` is the one
 * exception: the queue POISONS that row rather than holding it, so it lands as
 * a real `failed` and pins to "Picked up" like the other pre-download give-ups.
 */
export const KNOWN_BRIDGE_FAIL_REASONS: readonly string[] = [
  // daemon — deterministic
  'no_filename',
  'no_source_url',
  'routing_no_platen',
  'routing_no_entry',
  'routing_failed',
  // BLU-1439 (D-08): the bridge refused the artwork link for good (host not
  // on the org's allowlist, bad scheme, private address, ...). Reported once.
  'source_refused',
  // daemon — deterministic, Phase 86 platen variant outputs
  'platen_unknown',
  'platen_conflict',
  'platen_folder_missing',
  // daemon — transient (legacy daemons only)
  'download_failed',
  'resize_failed',
  'drop_failed',
  'upload_failed',
  'resolve_map_collision',
  // backend — give-ups
  'bridge_gave_up',
  'dual_unsupported',
  'back_only',
  'side_mismatch',
  'back_stem_unsupported',
  'unparseable_sku',
  'dual_no_catalogue_row',
  'dual_malformed_azure_row',
  'azure_not_configured',
  // backend — the standard-output contract POISONED the row (a real `failed`)
  'invalid_standard_targets',
  // backend — queue refusals: the bridge was never handed anything it could
  // fetch (the order's link is refused for good, or the issue lost its order)
  'artwork_link_rejected',
  'issue_incomplete',
  // backend — the bridge went quiet on a job a person started (F1)
  'bridge_not_picked_up',
  'bridge_stopped_responding',
  // backend — the standard-output contract HELD the row (`awaiting_bridge`)
  'awaiting_capable_bridge',
  'storage_not_bridgeable',
  'artwork_link_unavailable',
  'fleet_unknown',
  'no_arx4_targets',
  'outputs_already_present',
  'missing_source_side',
  'storage_probe_unavailable',
  'png_only_no_server_heal',
  // backend — Phase 86 platen variant holds (`awaiting_bridge`)
  'platen_variant_file_needed',
  'bridge_platen_outputs_unsupported',
];

/**
 * The hold codes that ride an `awaiting_bridge` stamp. Each has its own
 * standalone operator line (`issues.printFiles.awaitingBridgeReason.<code>`);
 * anything else falls back to the generic awaiting-bridge hint. Kept separate
 * from `KNOWN_BRIDGE_FAIL_REASONS` because a stale `failed` reason left on a
 * row that has since been held (`download_failed`, say) has no hold copy, and
 * looking one up would render the raw key.
 */
export const AWAITING_BRIDGE_HOLD_REASONS: ReadonlySet<string> = new Set([
  'awaiting_capable_bridge',
  'storage_not_bridgeable',
  'artwork_link_unavailable',
  'fleet_unknown',
  'no_arx4_targets',
  'outputs_already_present',
  'missing_source_side',
  'storage_probe_unavailable',
  // Phase 86: the size needs its own platen variant file and none is uploaded
  // (never sent with the generic artwork), or the file exists but the polling
  // bridge is too old to print it exactly.
  'platen_variant_file_needed',
  'bridge_platen_outputs_unsupported',
]);

/**
 * Holds where the bridge is not waiting on anything — it has nothing to make.
 * These are NOT failures and NOT a wait, so the row's badge must not claim
 * either; it says so plainly instead (`awaitingBridgeNoWorkPill`).
 */
export const AWAITING_BRIDGE_NO_WORK_REASONS: ReadonlySet<string> = new Set([
  'no_arx4_targets',
  'outputs_already_present',
]);

/**
 * Known reasons that pin the failure to ONE stepper row regardless of what
 * the run reported before. Deliberately NOT every known reason:
 * `bridge_gave_up` is absent because pinning it to "Picked up" walked the
 * stepper BACKWARDS — a run that had reported `downloaded`/`ripped` and then
 * died transiently hit the cap, and its green rows went grey while "Picked
 * up" turned red.
 */
const FAILED_STEP_BY_REASON: Readonly<Partial<Record<string, BridgeFetchStep>>> = {
  // daemon — deterministic
  no_filename: 'served',
  routing_no_platen: 'served',
  routing_no_entry: 'served',
  routing_failed: 'served',
  // BLU-1439: refused at the download step.
  source_refused: 'downloaded',
  platen_unknown: 'served',
  platen_conflict: 'served',
  platen_folder_missing: 'served',
  // daemon — transient (legacy daemons only)
  download_failed: 'downloaded',
  resize_failed: 'ripped',
  drop_failed: 'ripped',
  upload_failed: 'uploaded',
  resolve_map_collision: 'uploaded',
  // backend — give-ups that fail before any download
  dual_unsupported: 'served',
  back_only: 'served',
  side_mismatch: 'served',
  back_stem_unsupported: 'served',
  unparseable_sku: 'served',
  dual_no_catalogue_row: 'served',
  dual_malformed_azure_row: 'served',
  azure_not_configured: 'served',
  invalid_standard_targets: 'served',
  png_only_no_server_heal: 'served',
  // backend — queue refusals: nothing was ever handed out, so the run stops at
  // "Sent". `no_source_url` moved here too: the queue (and the requeue) now
  // refuse it before any hand-out; an old daemon's report means the same.
  no_source_url: 'queued',
  artwork_link_rejected: 'queued',
  issue_incomplete: 'queued',
  // backend — the bridge never picked the job up (F1)
  bridge_not_picked_up: 'served',
};

/**
 * Known reasons that fail AT the last step the run reached, not the row after
 * it: the backend gave up because the bridge stopped making progress, so the
 * last reported step is where it died. Falls back to "Picked up" when the
 * run never reported past `queued`.
 */
const FAIL_AT_LAST_PROGRESS_REASONS: ReadonlySet<string> = new Set([
  'bridge_gave_up',
  // F1: the bridge went silent after the last step it reported.
  'bridge_stopped_responding',
]);

/** Failures where a bare "Try again" would re-run the same dead end — the
 *  operator has to supply a link or a file first. */
const NOTHING_TO_RETRY_REASONS: ReadonlySet<string> = new Set([
  'no_source_url',
  'no_filename',
  'artwork_link_rejected',
  'issue_incomplete',
]);

export function isNothingToRetryReason(reason: string | null): boolean {
  const code = knownBridgeFailReason(reason);
  return code !== null && NOTHING_TO_RETRY_REASONS.has(code);
}

function reasonCode(reason: string | null): string | null {
  if (!reason) return null;
  // Reasons may carry a suffix (`download_failed: 404`) — key on the code.
  const code = reason.split(/[\s:]/)[0]?.toLowerCase() ?? '';
  return code.length > 0 ? code : null;
}

export function knownBridgeFailReason(reason: string | null): string | null {
  const code = reasonCode(reason);
  return code && KNOWN_BRIDGE_FAIL_REASONS.includes(code) ? code : null;
}

/** BLU-1309 (F26): a heal MUTATION's failure reason → its operator words.
 *  ONE map for the paste dialog and the Self-Heal Now toast. */
const HEAL_FAIL_REASON_KEYS: Readonly<Record<string, string>> = {
  download_failed: 'issues.printFiles.artworkUrl.failDownload',
  resize_failed: 'issues.printFiles.artworkUrl.failResize',
  write_failed: 'issues.printFiles.artworkUrl.failStorage',
  verify_failed: 'issues.printFiles.artworkUrl.failStorage',
  storage_unavailable: 'issues.printFiles.artworkUrl.failStorage',
  no_source_url: 'issues.printFiles.selfHealFailedNoSource',
  not_an_image: 'issues.printFiles.artworkUrl.failNotImage',
  too_large: 'issues.printFiles.artworkUrl.failTooLarge',
  artwork_link_rejected: 'issues.printFiles.artworkUrl.failLinkRejected',
  // F2: another check for this issue is still running.
  already_running: 'issues.printFiles.selfHealFailedAlreadyRunning',
};

/** The i18n key for a heal mutation's failure reason; anything unmapped
 *  (`concurrency_cap`, a new code) reads as `fallbackKey`. */
export function healFailReasonKey(
  reason: string | null | undefined,
  fallbackKey = 'issues.printFiles.selfHealFailedGeneric',
): string {
  return reason && Object.prototype.hasOwnProperty.call(HEAL_FAIL_REASON_KEYS, reason)
    ? HEAL_FAIL_REASON_KEYS[reason]!
    : fallbackKey;
}

/**
 * The hold code an `awaiting_bridge` row carries, or null when it carries
 * something we have no hold copy for. Callers render
 * `issues.printFiles.awaitingBridgeReason.<code>` on a hit and the generic
 * `issues.printFiles.awaitingBridgeHint` on a miss.
 */
export function awaitingBridgeHoldReason(reason: string | null | undefined): string | null {
  const code = knownBridgeFailReason(reason ?? null);
  return code && AWAITING_BRIDGE_HOLD_REASONS.has(code) ? code : null;
}

/** True when the hold means "nothing for the bridge to make", not "waiting". */
export function isNoBridgeWorkReason(reason: string | null | undefined): boolean {
  const code = awaitingBridgeHoldReason(reason);
  return code !== null && AWAITING_BRIDGE_NO_WORK_REASONS.has(code);
}

/**
 * Which stepper row a `failed` state paints red. A reason pinned to a row
 * wins; a give-up (`bridge_gave_up`) fails at the last step the run reached;
 * anything else fails on the row after the last non-failed state we saw; with
 * nothing known, "Picked up" (the bridge had it and could not proceed).
 * Never earlier than the last progress — a failure must not un-tick rows.
 */
export function inferFailedStep(
  lastProgress: BridgeFetchState | null,
  reason: string | null,
): BridgeFetchStep {
  const code = knownBridgeFailReason(reason);
  const pinned = code ? FAILED_STEP_BY_REASON[code] : undefined;
  if (pinned) return pinned;
  if (code && FAIL_AT_LAST_PROGRESS_REASONS.has(code)) {
    return lastProgress && lastProgress !== 'failed' && lastProgress !== 'queued'
      ? lastProgress
      : 'served';
  }
  if (lastProgress && lastProgress !== 'failed') {
    const idx = BRIDGE_FETCH_STEPS.indexOf(lastProgress);
    return BRIDGE_FETCH_STEPS[Math.min(idx + 1, BRIDGE_FETCH_STEPS.length - 1)]!;
  }
  return 'served';
}

/**
 * "12s ago" for the current row: a server stamp rendered relative to `now`.
 * Null when there is no (parseable) stamp. Clock skew clamps to 0 rather than
 * going negative. Units: seconds under a minute, minutes under an hour, else
 * hours — the bridge polls once a minute, so seconds matter at first and
 * hours only ever appear on a reopened stale run.
 */
export function formatRelativeAgo(
  at: string | null,
  now: number,
): { count: number; unit: 's' | 'm' | 'h' } | null {
  if (!at) return null;
  const then = Date.parse(at);
  if (!Number.isFinite(then)) return null;
  const secs = Math.max(0, Math.floor((now - then) / 1_000));
  if (secs < 60) return { count: secs, unit: 's' };
  const mins = Math.floor(secs / 60);
  if (mins < 60) return { count: mins, unit: 'm' };
  return { count: Math.floor(mins / 60), unit: 'h' };
}

/**
 * Build a snapshot from the LIST read model's typed projection
 * (`ProductionIssue.bridgeFetchState/At/FailReason` — the DAO's
 * `readBridgeFetchProgress`). The list surfaces (row badge, the dialog's seed)
 * read these; `snapshotFromMetadata` below is for the single-row read the
 * poll uses, which echoes raw metadata.
 */
export function snapshotFromFields(
  state: string | null | undefined,
  at: string | null | undefined,
  reason: string | null | undefined,
): BridgeFetchSnapshot | null {
  if (!isBridgeFetchUiState(state) || state === 'resolved') return null;
  return {
    state,
    at: typeof at === 'string' && at.length > 0 ? at : null,
    reason: state === 'failed' && typeof reason === 'string' && reason.length > 0 ? reason : null,
  };
}

/** Read a snapshot off an issue row's (safe) metadata, or null when unstamped. */
export function snapshotFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): BridgeFetchSnapshot | null {
  const state = metadata?.bridge_fetch_state;
  if (!isBridgeFetchUiState(state) || state === 'resolved') return null;
  const at = metadata?.bridge_fetch_state_at;
  const reason = metadata?.bridge_fetch_fail_reason;
  return {
    state,
    at: typeof at === 'string' ? at : null,
    reason: state === 'failed' && typeof reason === 'string' && reason.length > 0 ? reason : null,
  };
}
